import {isFeatureEnabled, isFeatureEnabledWithOptOut} from '@github-ui/feature-flags'

export const QUICK_CHAT_TASK_VIEW_FEATURE_FLAGS = [
  'one_copilot_chat',
  'copilot_mission_control_environments_web_kickoff',
] as const

const embeddedCopilotWebIntegrationCutoverRoots = new Set<string | symbol>()

// React partial props are not copied into the client feature flag environment.
export function setEmbeddedCopilotWebIntegrationCutover(rootId: string | symbol, enabled: boolean) {
  if (typeof window === 'undefined') return

  if (enabled) {
    embeddedCopilotWebIntegrationCutoverRoots.add(rootId)
  } else {
    embeddedCopilotWebIntegrationCutoverRoots.delete(rootId)
  }
}

export function isCopilotWebIntegrationEnabled() {
  return (
    copilotFeatureFlags.oneCopilotChat &&
    (isFeatureEnabled('copilot_web_integration_cutover') || embeddedCopilotWebIntegrationCutoverRoots.size > 0)
  )
}

/**
 * IMPORTANT NOTICE: Feature flags added here must also be added to the `packages/feature-flags/client-feature-flags.ts` file
 * or they will not be available to check in this file!
 */

class CopilotFeatureFlags {
  get browserTelemetry() {
    return isFeatureEnabled('one_copilot_browser_telemetry')
  }

  get earlyTaskProvisioning() {
    return isFeatureEnabled('copilot_chat_early_task_provisioning')
  }

  get oneCopilotChat() {
    return isFeatureEnabledWithOptOut('one_copilot_chat', 'one_copilot_chat_ui_opt_out')
  }

  get mcpLoadingStatus() {
    return isFeatureEnabled('one_copilot_mcp_loading_status')
  }

  get oneCopilotChatOwnerSplitPicker() {
    return isFeatureEnabled('one_copilot_chat_owner_split_picker')
  }

  get oneCopilotEnvironmentStripTour() {
    return isFeatureEnabled('one_copilot_environment_strip_tour')
  }

  get oneCopilotEnvironmentStripTourIgnoreDismiss() {
    return isFeatureEnabled('one_copilot_environment_strip_tour_ignore_dismiss')
  }

  get agentsRepoExplorer() {
    return isFeatureEnabled('copilot_agents_repo_explorer')
  }

  get taskPinning() {
    return (
      isFeatureEnabled('copilot_mission_control_pinned_tasks') &&
      isFeatureEnabled('copilot_mission_control_use_api_gateway')
    )
  }

  get appRemoteSessions() {
    return isFeatureEnabled('copilot_app_remote_sessions')
  }

  get webIntegrationCutover() {
    return isCopilotWebIntegrationEnabled()
  }

  get quickChatTaskViewEnabled() {
    return this.oneCopilotChat && QUICK_CHAT_TASK_VIEW_FEATURE_FLAGS.every(flag => isFeatureEnabled(flag))
  }

  get forceLegacyChatDefaultModel() {
    return isFeatureEnabled('copilot_api_force_legacy_base_chat_model')
  }

  get clearModelSelectionForDefaultChange() {
    return isFeatureEnabled('copilot_chat_clear_model_selection_for_default_change')
  }

  /**
   * When enabled, copilot textarea inputs size themselves purely in CSS (grid-stacking +
   * `field-sizing`) instead of the legacy per-keystroke JS autosize. Shared across the chat input and
   * the Mission Control composer/steering inputs; gated so the layout rewrite can roll out safely with
   * a fast fallback to the JS path.
   * @returns {boolean} Whether the feature is enabled.
   */
  get cssTextareaAutosize() {
    return isFeatureEnabled('copilot_css_textarea_autosize')
  }

  get customCopilots() {
    return isFeatureEnabled('copilot_custom_copilots')
  }

  get customCopilotsFeaturePreview() {
    return isFeatureEnabled('copilot_custom_copilots_feature_preview')
  }

  get copilotDuplicateThread() {
    return isFeatureEnabled('copilot_duplicate_thread')
  }

  get copilotShareActiveSubthread() {
    return isFeatureEnabled('copilot_share_active_subthread')
  }

  get copilotChatO1Tools() {
    return isFeatureEnabled('copilot_api_tools_for_non_streaming_models')
  }

  get staffPromptDialog() {
    return isFeatureEnabled('copilot_staff_prompt_dialog')
  }

  get freeToPaidTelemetry() {
    return isFeatureEnabled('copilot_free_to_paid_telem')
  }

  get freeToPaidSettingsUpgrade() {
    return isFeatureEnabled('copilot_ftp_settings_upgrade')
  }

  get freeToPaidYourCopilotSettings() {
    return isFeatureEnabled('copilot_ftp_your_copilot_settings')
  }

  get freeToPaidUpgradeToProFromModels() {
    return isFeatureEnabled('copilot_ftp_upgrade_to_pro_from_models')
  }

  get spacesImagesEnabled() {
    return isFeatureEnabled('copilot_custom_copilots_images')
  }

  get disableModelPickerWhileStreaming() {
    return isFeatureEnabled('copilot_chat_disable_model_picker_while_streaming')
  }

  get requestTracing() {
    return isFeatureEnabled('copilot_api_staff_only_tracing')
  }

  /** Bind a new Quick Chat's sandbox to the repository attached in the composer. */
  get quickChatProjectSessions() {
    return isFeatureEnabled('copilot_quick_chat_project_sessions')
  }

  /** Default a new Quick Chat to Chat while keeping the current page as implicit context. */
  get quickChatDefaultChatScope() {
    return isFeatureEnabled('copilot_quick_chat_default_chat_scope')
  }

  /** Limit a space's private sources to repositories owned by the space owner. */
  get spacesOrgScoping() {
    return isFeatureEnabled('spaces_org_scoping')
  }

  get spacesIndividualPoliciesEnabled() {
    return isFeatureEnabled('copilot_spaces_individual_policies_ga')
  }

  get sweAgentUseSubagents() {
    return isFeatureEnabled('copilot_swe_agent_use_subagents')
  }

  get visionGAGate() {
    return isFeatureEnabled('copilot_chat_vision_dotcom_chat_ga_gate')
  }

  /**
   * Gates the per-model reasoning effort picker shown next to the model picker.
   * Shares the CMC backend flag so the UI and the data-plane plumbing roll out together.
   */
  get codingAgentReasoningEffort() {
    return isFeatureEnabled('copilot_mission_control_reasoning_effort')
  }

  /** Gates the automation write-scope control; shares the backend enforcement flag. */
  get automationResourceScopedWrites() {
    return isFeatureEnabled('copilot_swe_agent_automation_resource_scoped_writes')
  }

  get codingAgentThirdPartyModelUI() {
    return isFeatureEnabled('coding_agent_third_party_model_ui')
  }

  get codingAgentHideModelPickerIfOnlyAuto() {
    return isFeatureEnabled('copilot_swe_agent_hide_model_picker_if_only_auto')
  }

  /** Gates the ownership picker on the automation creation form. */
  get automationRepositoryOwnershipAvailable() {
    return isFeatureEnabled('copilot_swe_agent_automation_repository_owned')
  }

  get prChatThreadPersistence() {
    return isFeatureEnabled('copilot_pr_chat_thread_persistence')
  }

  get upgradeFreezeEnabled() {
    return isFeatureEnabled('copilot_upgrade_freeze')
  }

  get copilotTokenBasedBilling() {
    return isFeatureEnabled('copilot_token_based_billing')
  }

  get ipmUbbIndividualBudgetBanner() {
    return isFeatureEnabled('ipm_ubb_individual_budget_banner')
  }

  get noImplicitContext() {
    return isFeatureEnabled('copilot_no_implicit_context')
  }

  get userCanUpgradePlanField() {
    return isFeatureEnabled('copilot_user_can_upgrade_plan_field')
  }

  get autoModeV2() {
    return isFeatureEnabled('copilot_chat_auto_mode_v2')
  }

  get xcodeInstallPage() {
    return isFeatureEnabled('copilot_xcode_install_page')
  }
}

export const copilotFeatureFlags = new CopilotFeatureFlags()
export type {CopilotFeatureFlags}
